import posthog from "posthog-js";
import type { CaptureResult, Properties } from "posthog-js";
import { environment } from "../configuration";
import type { UserEntity } from "../services/users/user.types";

const URL_LIKE_KEY = /url|referrer|pathname/i;

function stripUrlNoise(value: string): string {
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split("?")[0].split("#")[0];
  }
}

function sanitizeProperties(properties: Properties): Properties {
  const sanitized: Properties = { ...properties };
  for (const [key, value] of Object.entries(sanitized)) {
    if (
      typeof value === "string" &&
      (URL_LIKE_KEY.test(key) || /^https?:\/\//i.test(value))
    ) {
      sanitized[key] = stripUrlNoise(value);
    }
  }
  return sanitized;
}

/**
 * Strips query strings/hashes from `$current_url`, `$pathname`, `$referrer`,
 * and any other URL-shaped property before the event leaves the browser.
 */
export function beforeSend(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event) return event;
  return {
    ...event,
    properties: sanitizeProperties(event.properties),
  };
}

let initialized = false;

export function initBackOfficeAnalytics(): void {
  if (initialized) return;
  if (!environment.posthogKey) return;

  posthog.init(environment.posthogKey, {
    api_host: environment.posthogHost,
    // Exceptions-only: no autocapture, no pageviews, no replay (D3/D10 scope
    // — back-office is a new surface and gets none of D10's exception).
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    // No client-side identify until the Phase 4 identity endpoint exists
    // (see registerBackOfficeContext below), so PostHog never links events
    // to a person profile.
    person_profiles: "never",
    before_send: beforeSend,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
  });

  initialized = true;
}

/**
 * Registers non-identifying, D3-allowlisted super properties (role,
 * school_id, app) as PostHog persistent properties, WITHOUT calling
 * `identify()`. This is intentionally not a person-level identity: the
 * client-side SHA-256(salt+id) pseudonym was removed because the salt ships
 * in the bundle, making it reversible. A real server-derived HMAC pseudonym
 * lands with the Phase 4 identity endpoint (D-7); until then, exceptions are
 * captured anonymously with these dimensions attached.
 */
export function registerBackOfficeContext(user: UserEntity): void {
  posthog.register({
    role: user.role,
    school_id: user.school_id ?? null,
    app: "back-office",
  });
}

export function resetBackOfficeAnalytics(): void {
  posthog.reset();
}

function extractCorrelationId(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) return undefined;
  const response = (error as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (typeof data !== "object" || data === null) return undefined;
  const correlationId = (data as { correlationId?: unknown }).correlationId;
  return typeof correlationId === "string" ? correlationId : undefined;
}

export function reportBackOfficeError(
  error: unknown,
  context: Record<string, unknown> = {},
): void {
  try {
    const correlationId = extractCorrelationId(error);
    posthog.captureException(error, {
      ...context,
      ...(correlationId ? { correlation_id: correlationId } : {}),
      environment: process.env.NODE_ENV ?? "unknown",
    });
  } catch {
    // PostHog unavailable; the error already surfaced elsewhere (console, UI).
  }
}
